import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Banner, Button, Card, Dialog, Field, formatMoney, Money, PageHeader, Pill, Tabs, type Tone } from '@elysian/ui';
import type { Contact, RecurringRun, RecurringTemplate, TaxCode } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso, shortDate, unknownMergeFields } from '../invoices/shared';

const RUN: Record<RecurringRun['status'], { tone: Tone; label: string }> = {
  awaiting_approval: { tone: 'warn', label: 'Waiting for you' },
  posted: { tone: 'ok', label: 'Posted' },
  rejected: { tone: 'draft', label: 'Rejected' },
  skipped: { tone: 'draft', label: 'Skipped' },
  failed: { tone: 'danger', label: 'Failed' },
};
const MODE: Record<NonNullable<RecurringTemplate['mode']>, string> = {
  approve: 'You approve each one',
  auto_post: 'Posted automatically',
  auto_post_send: 'Posted and emailed automatically',
};
const EVERY: Record<RecurringTemplate['frequency'], string> = { weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

type View = 'waiting' | 'templates' | 'history';

export function Recurring() {
  const { source, today } = useSession();
  const toast = useToast((s) => s.show);
  const [view, setView] = useState<View>('waiting');
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const data = useAsync(async () => {
    const [runs, templates, customers, suppliers, codes] = await Promise.all([
      source.recurringRuns(), source.recurring(), source.contacts('customer'), source.contacts('supplier'), source.taxCodes(iso(today())),
    ]);
    return { runs, templates, contacts: [...customers, ...suppliers], customers, codes };
  }, [source, version], { keep: true });

  const runs = data.state === 'ready' ? data.data.runs : [];
  const waiting = runs.filter((r) => r.status === 'awaiting_approval');
  const reload = () => setVersion((v) => v + 1);

  async function act(id: string, work: () => Promise<unknown>, done: string) {
    setBusy(id);
    setError(null);
    try {
      await work();
      toast(done);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader title="Recurring" subtitle="Invoices and bills that repeat: contracts, rent, subscriptions"
        actions={<Button variant="primary" disabled={data.state !== 'ready'} onClick={() => setAdding(true)}>New recurring invoice</Button>} />
      {data.state === 'error' && <Banner>Couldn’t load: {data.error.message}</Banner>}
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}
      <Card>
        <Tabs<View> label="Show" value={view} onChange={setView} tabs={[
          { key: 'waiting', label: 'Waiting for approval', count: waiting.length },
          { key: 'templates', label: 'Templates', count: data.state === 'ready' ? data.data.templates.length : undefined },
          { key: 'history', label: 'History' },
        ]} />
        <div className="el-table-wrap">
          {view === 'waiting' && (
            <table className="el-table" aria-label="Runs waiting for approval">
              <tbody>
                {waiting.map((r) => (
                  <tr key={r.id}>
                    <td><strong>{r.template_name}</strong><div className="el-hint">{r.contact_name} · due to run {shortDate(r.run_date)}{r.document_id && <> · <Link to={r.doc_type === 'sales_invoice' ? `/invoices/${r.document_id}` : '#'}>see the draft</Link></>}</div></td>
                    <td className="el-num"><Money value={r.gross_total ?? '0'} /></td>
                    <td>
                      <div className="el-match" style={{ justifyContent: 'flex-end' }}>
                        <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => void act(r.id, () => source.declineRun(r.id, true), `${r.template_name}: this one skipped`)}>Skip this one</Button>
                        <Button size="sm" disabled={busy === r.id} onClick={() => void act(r.id, () => source.approveRun(r.id, false), `${r.template_name} posted`)}>Approve</Button>
                        {r.doc_type === 'sales_invoice' && (
                          <Button size="sm" variant="primary" disabled={busy === r.id} onClick={() => void act(r.id, () => source.approveRun(r.id, true), `${r.template_name} posted and emailed`)}>Approve and send</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {data.state === 'ready' && waiting.length === 0 && <tr><td className="el-muted">Nothing waiting. New runs appear here on their date.</td></tr>}
              </tbody>
            </table>
          )}
          {view === 'templates' && data.state === 'ready' && (
            <table className="el-table" aria-label="Recurring templates">
              <tbody>
                {data.data.templates.map((t) => (
                  <tr key={t.id}>
                    <td><strong>{t.name}</strong><div className="el-hint">{data.data.contacts.find((c) => c.id === t.contact_id)?.name} · {EVERY[t.frequency]} · {MODE[t.mode ?? 'approve']}</div></td>
                    <td>{t.status === 'active' ? <Pill tone="ok">Active</Pill> : <Pill tone="draft">{t.status === 'paused' ? 'Paused' : 'Ended'}</Pill>}</td>
                    <td className="el-num">{t.status === 'active' && t.next_run_date && <>Next {shortDate(t.next_run_date)}</>}</td>
                    <td className="el-num">{t.status === 'active' && <Button size="sm" variant="ghost" disabled={busy === t.id} onClick={() => void act(t.id!, () => source.runRecurringNow(t.id!), `${t.name}: a run is waiting for approval`)}>Run now</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {view === 'history' && (
            <table className="el-table" aria-label="Recurring history">
              <tbody>
                {runs.filter((r) => r.status !== 'awaiting_approval').map((r) => (
                  <tr key={r.id}>
                    <td>{shortDate(r.run_date)}</td>
                    <td>{r.template_name}<div className="el-hint">{r.contact_name}{r.decided_by && ` · ${r.decided_by === 'automatic' ? 'automatic' : `by ${r.decided_by.replace('ledger:user:', '')}`}`}</div></td>
                    <td><Pill tone={RUN[r.status].tone}>{RUN[r.status].label}</Pill></td>
                    <td className="el-num"><Money value={r.gross_total ?? '0'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
      {adding && data.state === 'ready' && (
        <NewTemplate customers={data.data.customers} codes={data.data.codes} onClose={() => setAdding(false)}
          onSaved={(t) => { setAdding(false); toast(`${t.name} set up. First run ${shortDate(t.next_run_date ?? t.start_date)}`); setView('templates'); reload(); }} />
      )}
    </>
  );
}

function NewTemplate({ customers, codes, onClose, onSaved }: { customers: Contact[]; codes: TaxCode[]; onClose: () => void; onSaved: (t: RecurringTemplate) => void }) {
  const { source, today, can } = useSession();
  const [contact, setContact] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('Monitoring {{month}} {{year}}');
  const [price, setPrice] = useState('');
  const [tax, setTax] = useState('R135');
  const [frequency, setFrequency] = useState<RecurringTemplate['frequency']>('monthly');
  const [start, setStart] = useState(iso(today()));
  const [mode, setMode] = useState<NonNullable<RecurringTemplate['mode']>>('approve');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const unknown = unknownMergeFields(description).filter((f) => !['month', 'year', 'period_start', 'period_end'].includes(f));
  const c = customers.find((x) => x.id === contact);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await source.createRecurring({
        name: name.trim() || `${description.split('{{')[0]!.trim() || 'Recurring'} — ${c?.name ?? ''}`, doc_type: 'sales_invoice', contact_id: contact,
        lines: [{ description, quantity: '1', unit_price: price.trim(), account_code: '4020', tax_code: tax }],
        frequency, start_date: start, mode, email_to: mode === 'auto_post_send' && c?.email ? [c.email] : [], due_days: c?.payment_terms_days ?? 30,
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title="New recurring invoice" onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !contact || !price.trim() || unknown.length > 0} onClick={() => void save()}>Set it up</Button></>}>
      <Field id="rt-cust" label="Customer">
        <select id="rt-cust" className="el-input" value={contact} onChange={(e) => setContact(e.target.value)}>
          <option value="">Choose…</option>
          {customers.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      </Field>
      <Field id="rt-desc" label="Line" hint="{{month}} and {{year}} fill in for each invoice" error={unknown.length ? `Not a merge field: ${unknown.map((u) => `{{${u}}}`).join(', ')}` : null}>
        <input id="rt-desc" className="el-input" value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="el-form-grid">
        <Field id="rt-price" label="Net amount"><input id="rt-price" className="el-input el-num" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="85.00" /></Field>
        <Field id="rt-vat" label="VAT">
          <select id="rt-vat" className="el-input" value={tax} onChange={(e) => setTax(e.target.value)}>
            {codes.filter((t) => t.applies_to !== 'purchases').map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
          </select>
        </Field>
        <Field id="rt-freq" label="How often">
          <select id="rt-freq" className="el-input" value={frequency} onChange={(e) => setFrequency(e.target.value as RecurringTemplate['frequency'])}>
            {Object.entries(EVERY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field id="rt-start" label="First invoice"><input id="rt-start" type="date" className="el-input" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
      </div>
      <Field id="rt-mode" label="When it’s due" hint={mode !== 'approve' && !can('recurring.approve') ? 'Only someone who can approve recurring invoices can switch on automatic posting.' : undefined}>
        <select id="rt-mode" className="el-input" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
          {Object.entries(MODE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Field>
      <Field id="rt-name" label="Name (optional)"><input id="rt-name" className="el-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      {price && <p className="el-hint" style={{ margin: 0 }}>Each invoice: {formatMoney(price.match(/^\d+(\.\d{1,4})?$/) ? price : '0')} plus VAT.</p>}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
